import { useContext, useEffect, useState } from 'react';
import { Context as HelperContext } from '../../providers/HelperContextProvider';
import { useCartStorageController } from './useCartStorageController';
import { createFetchStoreCart } from './webstoreCheckoutUtilities';

const useCartShippingController = (
  identifier,
  {
    isAuthenticated,
    isAdminCheckout = false,
    fetchShippingOnAddressChange = false,
    shippingCostMissing = false,
    fetchUserCart
  } = {}
) => {
  const { state: helperState, setWebstoreShippingAddress } = useContext(
    HelperContext
  );
  const {
    getGuestCartStorage,
    setCartShippingAddress,
    setCartShippingOptions
  } = useCartStorageController({ isAdminCheckout });
  const [debouncedState, setDebounceState] = useState({
    key: null,
    loading: false,
    value: null
  });
  const { shippingAddress } = helperState.webstore;

  useEffect(() => {
    if (identifier && !shippingAddress) {
      const cart = getGuestCartStorage(identifier);
      // eslint-disable-next-line no-use-before-define
      onChangeShippingAddress(
        cart.shippingAddress || { country: 'CA', state: 'ON' },
        true
      );
    }
  }, [identifier]);

  useEffect(() => {
    if (
      fetchShippingOnAddressChange &&
      shippingAddress?.hasChanged &&
      !shippingAddress.skipDebounce
    ) {
      setDebounceState((c) => ({
        ...c,
        loading: true
      }));
      const handler = setTimeout(() => {
        setDebounceState((c) => ({
          ...c,
          value: shippingAddress
        }));
      }, 800);

      return () => {
        clearTimeout(handler);
      };
    }
    return () => {};
  }, [shippingAddress]);

  useEffect(() => {
    if (
      fetchShippingOnAddressChange &&
      debouncedState.value !== null &&
      identifier
    ) {
      if (debouncedState.value.country && debouncedState.value.postalCode) {
        const cart = getGuestCartStorage(identifier);
        if (
          cart.shippingAddress?.city !== debouncedState.value.city ||
          cart.shippingAddress?.postalCode !==
            debouncedState.value.postalCode ||
          cart.shippingAddress?.country !== debouncedState.value.country ||
          shippingCostMissing
        ) {
          // eslint-disable-next-line no-use-before-define
          onFetchUpdatedShippingCosts({
            ecomCustomerAddress: debouncedState.value
          });
        }

        setCartShippingAddress(identifier, debouncedState.value);
      }

      setDebounceState((c) => ({
        ...c,
        loading: false
      }));
    }
  }, [debouncedState.value]);

  const onChangeShippingAddress = (newAddress, skipDebounce = false) => {
    setWebstoreShippingAddress({
      ...shippingAddress,
      ...newAddress,
      skipDebounce,
      hasChanged: true
    });
  };

  const onShippingCallback = (shippingCosts) => {
    setCartShippingOptions(
      identifier,
      shippingCosts.map((s) => ({
        ecomExternalConnectionExternalId: s.ecomExternalConnectionExternalId,
        fkEcomExternalConnectionType: s.fkEcomExternalConnectionType,
        value: s.value,
        label: s.label
      }))
    );
  };

  const onFetchUpdatedShippingCosts = ({ ecomCustomerAddress }) => {
    const onFetchCart = createFetchStoreCart({
      isAuthenticated,
      getGuestCartStorage,
      onShippingCallback,
      fetchUserCart
    });
    onFetchCart(
      identifier,
      {
        ecomCustomerAddress,
        includeShipping: true
      },
      () => {}
    );
  };

  const onChangeShippingCosts = ({ ecomCustomerAddress, shippingOptions }) => {
    const onFetchCart = createFetchStoreCart({
      isAuthenticated,
      getGuestCartStorage,
      onShippingCallback,
      fetchUserCart
    });
    onFetchCart(identifier, {
      ecomCustomerAddress,
      shippingOptions,
      includeShipping: true
    });
  };

  return {
    shippingAddress,
    shippingLoading: debouncedState.loading,
    debouncedState: debouncedState.value,
    onChangeShippingAddress,
    onShippingCallback,
    onChangeShippingCosts
  };
};

export { useCartShippingController };
